import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import {
  AggFunc,
  BaseComponent,
  BaseEditFormComponent,
  DateFormatService,
  DeliveryDate,
  DeliveryMonthReferenceService,
  DeliveryPeriodReferenceService,
  FabricProcessingFabricReferenceService,
  FormatService,
  GridService,
  Guid,
  IdDescription,
  MakeUpReferenceService,
  ModalService,
  OkModalComponent,
  OrderLineStatusCheck,
  Product,
  ProductDetails,
  ProductPriceListConversion,
  ProductService,
  ProductTypeReferenceService,
  PurchaseContractLineSelect,
  PurchaseOrderLineDetails,
  PurchaseOrderLineHeader,
  PurchaseOrderLineProduct,
  PurchaseOrderLineService,
  PurchaseOrderStatusReferenceService,
  PurchasePriceFromPriceListRequest,
  PurchasePriceFromPriceListResponse,
  TmsFormlyService,
  UnitOfMeasureReferenceService,
  WarehouseReferenceService,
} from 'tms-lib';
import { PurchaseContractForOrder } from '../../../models';
import {
  PurchaseContractLineService,
  PurchaseOrderLineRequirementService,
  PurchaseOrderSalesAllocationService,
  PurchaseOrderService,
  UnitOfMeasureService,
} from '../../../services';

@Component({
  selector: 'app-purchase-order-line-details',
  templateUrl: './purchase-order-line-details.component.html',
  styleUrl: './purchase-order-line-details.component.scss',
})
export class PurchaseOrderLineDetailsComponent extends BaseComponent implements OnInit {
  @ViewChild(BaseEditFormComponent) form: BaseEditFormComponent;
  @Input() purchaseOrderLineId: string;
  @Input() purchaseOrderHeaderId: string;
  @Input() supplierId: string;
  @Input() productId: string;
  @Input() quantity: number;

  fields: FormlyFieldConfig[];
  productDetails: ProductDetails;

  private amendModel: PurchaseOrderLineHeader;

  private deliveryMonths$: Observable<IdDescription[]>;
  private deliveryPeriods$: Observable<IdDescription[]>;
  private fabricProcessingFabrics = new BehaviorSubject<IdDescription[]>([]);
  private makeUps$: Observable<IdDescription[]>;
  private purchaseContracts$: Observable<PurchaseContractLineSelect[]>;
  private purchaseContractSelectOptions$: Observable<IdDescription[]>;
  private purchaseOrderStatuses$: Observable<IdDescription[]>;
  private unitOfMeasures$: Observable<IdDescription[]>;
  private warehouses$: Observable<IdDescription[]>;
  private orderLineStatusError: string;

  columnDefs: ColDef[] = [
    this.gridService.text('purchaseOrderLineStatus', $localize`Purchase Order Line Status`),
    this.gridService.text('user', $localize`User`),
    this.gridService.dateTime('processedDate', $localize`Processed Date`),
  ];

  columnSalesAllocDefs: ColDef[] = [
    this.gridService.text('orderNo', $localize`Order Number`),
    this.gridService.text('customerKey', $localize`Customer Key`),
    this.gridService.text('customerName', $localize`Customer Name`),
    this.gridService.numberFormat('allocated', $localize`Allocated`, { aggFunc: AggFunc.sum }),
    this.gridService.date('deliveryDate', $localize`Delivery Date`),
  ];

  columnPurchaseAllocDefs: ColDef[] = [
    this.gridService.text('orderNo', $localize`Order Number`),
    this.gridService.text('supplierKey', $localize`Supplier Key`),
    this.gridService.text('supplierName', $localize`Supplier Name`),
    this.gridService.text('productKey', $localize`Product`),
    this.gridService.text('productDescription', $localize`Description`),
    this.gridService.numberFormat('allocated', $localize`Allocated`, { aggFunc: AggFunc.sum }),
    this.gridService.date('deliveryDate', $localize`Delivery Date`),
  ];

  // @ts-ignore
  constructor(
    public purchaseOrderLineService: PurchaseOrderLineService,
    private deliveryMonthReferenceService: DeliveryMonthReferenceService,
    private deliveryPeriodReferenceService: DeliveryPeriodReferenceService,
    private fabricProcessingFabricReferenceService: FabricProcessingFabricReferenceService,
    private makeUpReferenceService: MakeUpReferenceService,
    private productService: ProductService,
    private productTypeReferenceService: ProductTypeReferenceService,
    private purchaseContractLineService: PurchaseContractLineService,
    private purchaseOrderService: PurchaseOrderService,
    private purchaseOrderStatusReferenceService: PurchaseOrderStatusReferenceService,
    private purchaseOrderLineRequirementService: PurchaseOrderLineRequirementService,
    private purchaseOrderSalesAllocationService: PurchaseOrderSalesAllocationService,
    private unitOfMeasureReferenceService: UnitOfMeasureReferenceService,
    private unitOfMeasureService: UnitOfMeasureService,
    private warehouseReferenceService: WarehouseReferenceService,
    private formlyService: TmsFormlyService,
    private gridService: GridService,
    private modalService: ModalService,
    private formatService: FormatService,
    private dateFormatService: DateFormatService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getReferenceData();
    if (this.purchaseOrderLineId) {
      this.getProductDetails(this.productId);
    }
    this.getFields();

    this.purchaseOrderLineService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.form.getModel());
  }

  get model(): PurchaseOrderLineDetails {
    return this.form?.model;
  }

  saved() {
    this.purchaseOrderService.refreshDetails.next();
    this.purchaseOrderLineService.refreshDetails.next();
    this.purchaseOrderLineRequirementService.refreshDetails.next();
  }

  private getReferenceData() {
    this.deliveryMonths$ = this.deliveryMonthReferenceService.get();
    this.deliveryPeriods$ = this.deliveryPeriodReferenceService.get();
    this.makeUps$ = this.makeUpReferenceService.get();
    this.purchaseContracts$ = this.purchaseContractLineService.getSelectItems(
      this.supplierId,
      this.productId ?? Guid.empty,
    );
    this.purchaseOrderStatuses$ = this.purchaseOrderStatusReferenceService.get();
    this.unitOfMeasures$ = this.unitOfMeasureReferenceService.get();
    this.warehouses$ = this.warehouseReferenceService.get();

    this.purchaseContractSelectOptions$ = this.purchaseContracts$.pipe(
      map((contracts) =>
        contracts.map((contract) => ({
          id: contract.id,
          description: `${contract.contractNo} ${contract.productKey} ${contract.productDescription} ${contract.unitOfMeasure} ${contract.contractLineBalance} ${contract.price} ${contract.availableDate} ${contract.expiryDate}`,
        })),
      ),
    );
  }

  private getFields() {
    this.fields = [
      ...this.formlyService.threeColumnGrid(
        [
          this.formlyService.textReadonly('sequence', $localize`Sequence`),
          this.formlyService.select(
            'purchaseContractLineId',
            $localize`Contract`,
            this.purchaseContractSelectOptions$,
            undefined,
            {
              change: (field: FormlyFieldConfig, event) => this.selectContract(event.value),
              cssClass: 'width-200',
            },
          ),
          this.formlyService.textReadonly(
            'contractDetails',
            $localize`Contract Details`,
            undefined,
            undefined,
            (field: FormlyFieldConfig) => !field.model?.purchaseContractLineId,
          ),
          ...this.formlyService.productSearch(
            'productSearch',
            'productId',
            (model: Product) => this.changeProduct(model),
            () => this.clearProduct(),
            (model: PurchaseOrderLineDetails) => !!model.purchaseContractNumber,
            undefined,
            (model: string) => this.supplierId,
          ),
          this.formlyService.text('productTypeDescription', $localize`Product Type`, undefined, {
            readonly: true,
            required: true,
          }),
          this.formlyService.text('productKey', $localize`Product`, undefined, { readonly: true, required: true }),
          this.formlyService.text('productDescription', $localize`Description`, undefined, {
            readonly: true,
            required: true,
          }),
          this.formlyService.select('unitOfMeasureId', $localize`Unit of Measure`, this.unitOfMeasures$, undefined, {
            required: true,
            change: (field: FormlyFieldConfig, event) => this.changeUnitOfMeasure(field.model),
          }),
          this.formlyService.number('quantity', $localize`Quantity`, undefined, {
            required: true,
            change: (field: FormlyFieldConfig, event) => {
              this.updateRequirements(event.target.value);
              if (!field.model.purchaseContractLineId) {
                this.getPriceFromPriceList({
                  quantity: field.model.quantity,
                  cutPrice: field.model.cutPrice,
                } as PurchasePriceFromPriceListRequest).subscribe();
              }
            },
          }),
          this.formlyService.number('price', $localize`Price`, undefined, { required: true }, undefined, {
            'props.append': (field: FormlyFieldConfig) => field.model?.currencyCode,
          }),
          this.formlyService.number('noOfItems', $localize`Number of Items`, undefined, { required: true }),
          this.formlyService.select('makeUpId', $localize`Make Up`, this.makeUps$),
          this.formlyService.select(
            'purchaseOrderStatusId',
            $localize`Order Line Status`,
            this.purchaseOrderStatuses$,
            undefined,
            {
              required: true,
            },
            undefined,
            undefined,
            undefined,
            undefined,
            {
              checkStatus: {
                expression: (control: UntypedFormControl, field: FormlyFieldConfig) =>
                  this.purchaseOrderLineService
                    .getCheckStatusChangeAllowed(this.purchaseOrderLineId, control.value)
                    .pipe(
                      takeUntil(this.destroy$),
                      map((result: OrderLineStatusCheck) => {
                        this.orderLineStatusError = result.error;
                        return result.success;
                      }),
                    ),
                message: () => this.orderLineStatusError,
              },
            },
          ),
          this.formlyService.select('warehouseId', $localize`Warehouse`, this.warehouses$),
          this.formlyService.checkbox('nearestEighth', $localize`Round to Nearest Eighth`),
        ],
        [
          this.formlyService.text('supplierLineRef', $localize`Line Reference`),
          this.formlyService.text('ourRef1', $localize`Our Reference 1`),
          this.formlyService.text('ourRef2', $localize`Our Reference 2`),
          this.formlyService.text('ourRef3', $localize`Our Reference 3`),
          this.formlyService.textarea('supplierNotes', $localize`Supplier Notes`, undefined, { rows: 3 }),
          this.formlyService.textarea('internalNotes', $localize`Internal Notes`, undefined, { rows: 3 }),
          this.formlyService.textarea(
            'additionalProductComments',
            $localize`Additional Product Comments`,
            undefined,
            { rows: 2 },
            (field: FormlyFieldConfig) => !field.model.additionalProductComments,
          ),
          this.formlyService.select(
            'fabricProcessingFabricId',
            $localize`Fabric Process`,
            this.fabricProcessingFabrics,
            undefined,
            undefined,
            (field: FormlyFieldConfig) => this.fabricProcessingFabrics.value.length === 0,
          ),
        ],
        [
          this.formlyService.checkbox('defaultLeadTime', $localize`Default Lead Time`),

          this.formlyService.checkbox('nextDayDelivery', $localize`Next Day Delivery`, undefined, {
            change: (field: FormlyFieldConfig, event) =>
              this.updateDeliveryDate({ nextDayDelivery: field.model.nextDayDelivery } as DeliveryDate),
          }),
          this.formlyService.date('deliveryDate', $localize`Delivery Date`, undefined, {
            required: true,
            change: (field: FormlyFieldConfig, event) =>
              this.updateDeliveryDate({
                deliveryDate: this.dateFormatService.dateFromStringOrDate(event.target.value),
              } as DeliveryDate),
          }),
          this.formlyService.number('deliveryWeek', $localize`Delivery Week`, undefined, {
            required: true,
            change: (field: FormlyFieldConfig, event) =>
              this.updateDeliveryDate({ deliveryWeek: event.target.value } as DeliveryDate),
          }),
          this.formlyService.select('deliveryPeriodId', $localize`Delivery Period`, this.deliveryPeriods$, undefined, {
            required: true,
            change: (field: FormlyFieldConfig, event) =>
              this.updateDeliveryDate({
                deliveryPeriodId: event.value,
                deliveryDate: field.model.deliveryDate,
              } as DeliveryDate),
          }),
          this.formlyService.select('deliveryMonthId', $localize`Delivery Month`, this.deliveryMonths$, undefined, {
            required: true,
            change: (field: FormlyFieldConfig, event) =>
              this.updateDeliveryDate({
                deliveryMonthId: event.value,
                deliveryDate: field.model.deliveryDate,
              } as DeliveryDate),
          }),
          this.formlyService.number('deliveryYear', $localize`Delivery Year`, undefined, {
            required: true,
            change: (field: FormlyFieldConfig, event) =>
              this.updateDeliveryDate({
                deliveryYear: event.target.value,
                deliveryDate: field.model.deliveryDate,
              } as DeliveryDate),
          }),
          this.formlyService.textarea('deliveryNotes', $localize`Delivery Notes`),
          this.formlyService.date('eta', $localize`Estimated Delivery Date`),
          this.formlyService.date('promisedDate', $localize`Promised Date`),
          this.formlyService.checkbox('callOffReady', 'Call Off Ready'),
          this.formlyService.checkbox('callOffRequested', 'Call Off Requested'),
        ],
      ),
    ];
  }

  private changeProduct(model: Product) {
    this.productService
      .getProductForPurchaseOrder(model.id, this.supplierId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((product: PurchaseOrderLineProduct) => this.form.updateModel(product));
    this.getProductDetails(model.id);
  }

  private getProductDetails(productId: string) {
    if (!productId) {
      return;
    }
    this.productService
      .edit(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((productDetails: ProductDetails) => (this.productDetails = productDetails));

    this.fabricProcessingFabricReferenceService
      .getForFabric(productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((processes: IdDescription[]) => this.fabricProcessingFabrics.next(processes));
  }

  private clearProduct() {
    this.form.updateModel({
      productId: undefined,
      productKey: undefined,
      productDescription: undefined,
      productTypeDescription: undefined,
    });
  }

  private getPriceFromPriceList(
    model: PurchasePriceFromPriceListRequest,
  ): Observable<PurchasePriceFromPriceListResponse> {
    model.purchaseOrderHeaderId = this.purchaseOrderHeaderId;
    model.productId = this.model.productId;
    model.rrp = false;
    return this.purchaseOrderLineService.getPriceFromPriceList(model).pipe(
      takeUntil(this.destroy$),
      tap((priceFromPriceList: PurchasePriceFromPriceListResponse) => this.form.updateModel(priceFromPriceList)),
    );
  }

  private selectContract(purchaseContractLineId: string) {
    this.purchaseContractLineService
      .getForPurchaseOrder(purchaseContractLineId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((purchaseContract: PurchaseContractForOrder) =>
        this.form.updateModel({
          purchaseContractHeaderId: purchaseContract.purchaseContractHeaderId,
          purchaseContractLineId: purchaseContract.purchaseContractLineId,
          purchaseContractNumber: purchaseContract.purchaseContractNumber,
          productId: purchaseContract.productId,
          productKey: purchaseContract.productKey,
          productDescription: purchaseContract.productDescription,
          productTypeId: purchaseContract.productTypeId,
          productTypeDescription: purchaseContract.productTypeDescription,
          unitOfMeasureId: purchaseContract.unitOfMeasureId,
          price: purchaseContract.price,
        }),
      );
  }

  private updateDeliveryDate(model: DeliveryDate) {
    this.purchaseOrderLineService
      .deliveryDate(model)
      .pipe(takeUntil(this.destroy$))
      .subscribe((deliveryDate: DeliveryDate) => this.form.updateModel(deliveryDate));
  }

  private updateRequirements(quantity: number) {
    if (this.purchaseOrderLineId) {
      this.purchaseOrderLineService
        .checkForAllocations(this.purchaseOrderLineId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((allocations: boolean) => {
          if (allocations) {
            const { modal, component } = this.modalService.openMedium<OkModalComponent>(OkModalComponent);
            component.header = $localize`Purchase Order Line`;
            component.message = $localize`Order has allocations. These need to be de-allocated`;
            modal.result.then(
              () => this.form.updateModel({ quantity: this.quantity }),
              () => this.form.updateModel({ quantity: this.quantity }),
            );
          } else {
            this.amendOrder();
            this.purchaseOrderLineRequirementService
              .updateRequirements(this.purchaseOrderLineId, quantity)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                this.saved();
                this.quantity = quantity;
              });
          }
        });
    }
  }

  private amendOrder() {
    this.amendModel.id = this.model.id;
    this.amendModel.quantity = this.model.quantity;
    this.amendModel.price = this.model.price;
    this.purchaseOrderLineService
      .amendOrder(this.amendModel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.purchaseOrderService.refreshDetails.next();
        this.purchaseOrderLineService.refreshDetails.next();
      });
  }

  private changeUnitOfMeasure(model: any) {
    this.unitOfMeasureService
      .checkAlternateUnitOfMeasure(model.purchaseContractLineId, model.productTypeId, model.unitOfMeasureId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((priceListConversion: ProductPriceListConversion) =>
        this.form.updateModel({
          price: this.formatService.roundNumber(+model.price * priceListConversion.conversionQuantity, 2),
        }),
      );
  }
}
